import { useMemo } from 'react';
import { BoxProps } from '@apia/theme';
import { useLatest } from 'ahooks';
import { TComponent, TUseCustomEvents } from './types';
import { getEventHandler } from './getEventHandler';

/**
 * Este hook permite controlar el flujo de eventos entre un componente y su
 * padre, de forma que si el padre hace preventDefault del evento, las acciones
 * no se ejecuten.
 *
 * @example
 *
 * Flujo de ejemplo: onClick. component='button'
 *
 * Se llama a parentOnClick, si existe, con un nuevo evento.
 *
 * Si el parent llama a preventDefault, termina el flujo.
 *
 * Si el parent no llama a preventDefault, se llama al método onClick provisto.
 */
export default function useCustomEvents<
  ComponentType extends TComponent = 'input',
>(props: TUseCustomEvents<ComponentType>): BoxProps {
  const latestProps = useLatest(props);

  const inputProps = useMemo<BoxProps>(() => {
    return {
      onBlur: getEventHandler('blur', latestProps),
      onChange: getEventHandler('change', latestProps),
      onClick: getEventHandler('click', latestProps),
      onFocus: getEventHandler('focus', latestProps),
      onKeyDown: getEventHandler('keydown', latestProps),
      onKeyUp: getEventHandler('keyup', latestProps),
      onMouseDown: getEventHandler('mousedown', latestProps),
      onMouseUp: getEventHandler('mouseup', latestProps),
      name: latestProps.current.name,
      defaultValue: latestProps.current.defaultValue,
    };
  }, [latestProps]);

  return inputProps;
}
